/**
 * AddItemPage
 * Kitchly v0.5.0
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Plus,
  RotateCcw
} from 'lucide-react';
import { inventory, locations as locationsApi } from '../services/api';

const UNIT_OPTIONS = [
  'piece', 'kg', 'g', 'lb', 'oz',
  'l', 'ml', 'cup', 'tbsp', 'tsp',
  'bag', 'box', 'can', 'bottle', 'jar',
  'pack', 'bunch', 'loaf', 'dozen'
];

function AddItemPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    quantity: '1',
    unit: 'piece',
    location_id: '',
    notes: ''
  });
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [justAdded, setJustAdded] = useState(null);

  useEffect(() => {
    locationsApi.list()
      .then(data => setLocations(data.locations || []))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setLoading(true);
    try {
      await inventory.add({
        name: form.name.trim(),
        quantity: parseFloat(form.quantity) || 1,
        unit: form.unit,
        location_id: form.location_id ? parseInt(form.location_id) : undefined,
        notes: form.notes.trim() || undefined
      });

      const addedName = form.name.trim();
      toast.success(t('addItem.added', { name: addedName }));
      setJustAdded(addedName);

      // Reset form but keep location
      setForm(prev => ({
        name: '',
        quantity: '1',
        unit: 'piece',
        location_id: prev.location_id,
        notes: ''
      }));
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnother = () => {
    setJustAdded(null);
  };

  return (
    <div className="add-item-page" style={{ minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: 'var(--space-md)', gap: 'var(--space-md)' }}>
        <button className="btn-icon" onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ flex: 1, margin: 0 }}>{t('addItem.title')}</h1>
      </div>

      <div style={{ padding: '0 var(--space-md)' }}>
        {/* Success state */}
        {justAdded && (
          <div className="banner banner-success mb-lg">
            <span>"{justAdded}" {t('addItem.added', { name: '' }).replace('"" ', '')}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Item Name */}
          <div className="form-group">
            <label className="form-label" htmlFor="name">{t('addItem.name')}</label>
            <input
              id="name"
              name="name"
              type="text"
              className="form-input"
              placeholder={t('addItem.namePlaceholder')}
              value={form.name}
              onChange={handleChange}
              required
              autoFocus
            />
          </div>

          {/* Quantity + Unit (side by side) */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="quantity">{t('addItem.quantity')}</label>
              <input
                id="quantity"
                name="quantity"
                type="number"
                className="form-input"
                value={form.quantity}
                onChange={handleChange}
                min="0.01"
                step="0.01"
                required
                inputMode="decimal"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="unit">{t('addItem.unit')}</label>
              <select
                id="unit"
                name="unit"
                className="form-select"
                value={form.unit}
                onChange={handleChange}
              >
                {UNIT_OPTIONS.map(u => (
                  <option key={u} value={u}>{t(`units.${u}`, u)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Location */}
          <div className="form-group">
            <label className="form-label" htmlFor="location_id">{t('addItem.location')}</label>
            <select
              id="location_id"
              name="location_id"
              className="form-select"
              value={form.location_id}
              onChange={handleChange}
            >
              <option value="">{t('addItem.selectLocation')}</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label className="form-label" htmlFor="notes">{t('addItem.notes')}</label>
            <textarea
              id="notes"
              name="notes"
              className="form-input"
              placeholder={t('addItem.notesPlaceholder')}
              value={form.notes}
              onChange={handleChange}
              rows={3}
            />
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', marginTop: 'var(--space-lg)' }}>
            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading || !form.name.trim()}>
              <Plus size={18} />
              {loading ? t('common.loading') : t('addItem.add')}
            </button>

            {justAdded && (
              <button
                type="button"
                className="btn btn-secondary btn-full"
                onClick={handleAddAnother}
              >
                <RotateCcw size={16} /> {t('addItem.addAnother')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddItemPage;
